import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Button,
  ButtonVariantOption,
  ButtonColorOption,
} from "@/components/Button";
import { toast } from "react-toastify";
import { useDeletePlotRequestDetails } from "@/features/map/hooks/useDeletePlotRequestDetails";
import { useNavigate } from "react-router-dom";

import { formatNumberWithComma } from "@/utils/formatNumber";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedIds: number[];
  totalCount: number;
  onSuccess: () => void;
}

export const DeletePlotDetailsConfirmModal: React.FC<IProps> = ({
  open,
  setOpen,
  selectedIds,
  totalCount,
  onSuccess,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { trigger: deleteDetails } = useDeletePlotRequestDetails();
  const navigate = useNavigate();

  const handleClose = (): void => {
    if (!isDeleting) {
      setOpen(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (selectedIds.length === 0) {
      toast.error("削除対象が選択されていません");
      return;
    }

    setIsDeleting(true);
    try {
      await deleteDetails(selectedIds);

      toast.success(
        `${formatNumberWithComma(selectedIds.length)}件の行を削除しました`
      );

      // 削除後の残件数をチェック（フィルター前の全体件数で判定）
      const deletedCount = selectedIds.length;
      const newTotalCount = totalCount - deletedCount;

      if (newTotalCount === 0) {
        navigate("/map", { state: { openSavedPlotModal: true } });
      } else {
        onSuccess();
        handleClose();
      }
    } catch (e) {
      console.error(e);
      toast.error("削除に失敗しました");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">確認画面</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          選択された{formatNumberWithComma(selectedIds.length)}
          件の行を削除します。
        </DialogContentText>
        <DialogContentText>
          この操作は取り消せません。本当に削除しますか？
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          label="キャンセル"
          onClick={handleClose}
          variant={ButtonVariantOption.Outlined}
          color={ButtonColorOption.Primary}
          disabled={isDeleting}
        />
        <Button
          label="削除する"
          onClick={() => {
            void handleDelete();
          }}
          variant={ButtonVariantOption.Outlined}
          color={ButtonColorOption.Error}
          disabled={isDeleting}
        />
      </DialogActions>
    </Dialog>
  );
};
